import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import Image from 'next/image'
import { useRouter } from 'next/router'
import Collapse from '@mui/material/Collapse'
import {
  MdAdd,
  MdOutlineClose,
  MdPublish,
  MdOutlineRemoveRedEye,
  MdOutlineAccountBalance,
} from 'react-icons/md'
import { GoGlobe } from 'react-icons/go'
import { Modal, Button } from 'components/elements'
import AuthSteps from 'components/collections/AuthSteps'
import { getAlphaRgb } from 'components/layouts'
import { useSession } from 'context/Session'

import mypic from '../assets/advantage.jpg'

import {
  TypeContent,
  PrePrintContent,
  WhoReadsContent,
  BuildFollowingContent,
  HowGetWritingContent,
  WhyShouldContent,
  AssistContent,
} from './FAQ'

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledPanel = styled.div`
  background-color: ${({ theme, color }) => color || theme.colors.white};
  display: flex;
  width: 100%;
  justify-content: center;
  text-align: center;
`
const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 878px;
  padding: 88px 15px 96px;
  width: 100%;
  ${({ theme }) => theme.media.phone} {
    padding: 58px 15px 66px;
  }
`

const PanelTitle = styled.h1`
  ${({ theme }) => theme.font.h1}
  margin-top: 0;
  margin-bottom: 24px;
  span {
    color: ${({ theme }) => theme.colors.accentY};
  }
`
const PanelDesc = styled.h4`
  ${({ theme }) => theme.font.h4}
  margin: 0;
  font-weight: normal;
  margin-bottom: 24px;
`

const GetStartedButton = styled(Button)`
  width: 100%;
  max-width: 296px;
  height: 56px;
  margin: 0 auto;
`

const StartButton = () => {
  const router = useRouter()
  const [isOpen, setOpen] = useState(false)
  const { user } = useSession()
  const [stepsConfig, setStepsConfig] = useState({})

  const openSetupModal = () => {
    setStepsConfig({
      defaultStep: !user?.isProfileComplete ? 'setup' : 'welcome',
      defaultValues: user?.profile,
      stepCustomConfig: {
        setup: {
          desc: 'Complete your profile setup before writing an article',
        },
      },
    })
    setOpen(true)
  }

  return (
    <>
      <GetStartedButton
        onClick={() => {
          if (!user) {
            setOpen(true)
          } else {
            if (user?.isProfileComplete && user?.isEmailConfirmed) {
              router.push('/write')
            } else {
              openSetupModal()
            }
          }
        }}
      >
        Get Started
      </GetStartedButton>
      <Modal
        isOpen={isOpen}
        close={() => setOpen(false)}
        closeOnOutside={false}
      >
        <AuthSteps {...stepsConfig} />
      </Modal>
    </>
  )
}

export const FirstPanel = () => {
  return (
    <StyledPanel>
      <PanelContent>
        <PanelTitle>
          Bring <span>attention</span> to
          <br /> your research
        </PanelTitle>
        <PanelDesc>
          Our readership includes executives from every major pharmaceutical,
          biotech, investor and governmental health org. on the planet, along
          with thousands of people interested in cutting edge research. If you
          are biomedical researcher, there is no better place to bring attention
          and awareness to your work than by publishing on TrialSiteNews.
        </PanelDesc>
        <StartButton />
      </PanelContent>
    </StyledPanel>
  )
}

const AdvantageContent = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
  ${({ theme }) => theme.media.phone} {
    flex-direction: column;
  }
`
const AdvantegeList = styled.ul`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  text-align: left;
  margin-left: 15px;
  max-width: 488px;
  li + li {
    margin-top: 20px;
  }
`
const ImgContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 30px;
  max-width: 416px;
  ${({ theme }) => theme.media.phone} {
    margin-left: 0;
    margin-top: 20px;
  }
`
const ImgPreview = styled.div`
  width: 100%;
  height: auto;
  position: relative;
`
const ImgDesc = styled.div`
  ${({ theme }) => theme.font.body2}
  color: ${({ theme }) => theme.colors.grey};
  text-align: center;
  width: 100%;
  margin-top: 10px;
`

export const SecondPanel = () => (
  <StyledPanel color="#F1F1F3">
    <PanelContent>
      <PanelTitle>Get the TrialSite Advantage</PanelTitle>
      <AdvantageContent>
        <AdvantegeList>
          <li>
            Editorialize your work for lay people. If its hard to understand, it
            gets less attention
          </li>
          <li>
            Build a following to increase your impact. The more people know you,
            the more opportunity you have
          </li>
          <li>
            Get people excited. Your work is groundbreaking, it deserves
            attention
          </li>
          <li>
            Secure the research grants you need. Trialsite publishers have
            received tens of millions in funding
          </li>
        </AdvantegeList>
        <ImgContainer>
          <ImgPreview>
            <Image
              src={mypic}
              alt="Picture of the author"
              layout="responsive"
            />
          </ImgPreview>
          <ImgDesc>
            {`"Nobody read my work on pre-print servers, but a single article on
            TrialSite got me a $20m research grant!"`}
          </ImgDesc>
        </ImgContainer>
      </AdvantageContent>
    </PanelContent>
  </StyledPanel>
)

const ReasonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin: 20px -20px 40px;
`
const Reason = styled.div`
  width: 252px;
  height: 310px;
  background-color: ${({ theme }) => theme.colors.lightGrey};
  border: solid 1px ${({ theme, color }) => color || theme.colors.primary};
  padding: 30px 10px;
  position: relative;
  margin: 20px;
  overflow: hidden;
`
const ReasonContent = styled.div`
  height: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  box-shadow: 0 1rem 3rem rgb(31 45 61 / 13%);
`
const ReasonIcon = styled.div`
  width: 116px;
  height: 116px;
  margin-bottom: 20px;
  font-size: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, color }) => color || theme.colors.primary};
`
const ReasonLabel = styled.div`
  ${({ theme }) => theme.font.h3}
  text-align: left;
  color: ${({ theme, color }) => color || theme.colors.primary};
  padding: 0 30px 20px;
`
const ReasonGradient = styled.div`
  position: absolute;
  width: 10px;
  height: 50%;
  background: linear-gradient(#cce5ff, #007cff);
  top: 0;
  left: 0;
  ${({ placeOnEnd }) =>
    placeOnEnd &&
    css`
      top: 50%;
      left: calc(100% - 10px);
    `}

  ${({ color }) =>
    color &&
    css`
      background: linear-gradient(${getAlphaRgb(color, 0.1)}, ${color});
    `}
`

export const ThirdPanel = () => (
  <StyledPanel>
    <PanelContent style={{ maxWidth: '80vw' }}>
      <PanelTitle>
        Reasons to publish <br />
        on <span>TrialSite</span>
      </PanelTitle>
      <ReasonsContainer>
        <Reason>
          <ReasonGradient />
          <ReasonGradient placeOnEnd />
          <ReasonContent>
            <ReasonIcon>
              <GoGlobe />
            </ReasonIcon>
            <ReasonLabel>The right global audience</ReasonLabel>
          </ReasonContent>
        </Reason>
        <Reason color="#FAC409">
          <ReasonGradient color="#FAC409" />
          <ReasonGradient color="#FAC409" placeOnEnd />
          <ReasonContent>
            <ReasonIcon color="#FAC409">
              <MdPublish />
            </ReasonIcon>
            <ReasonLabel color="#FAC409">
              Completely free to publish
            </ReasonLabel>
          </ReasonContent>
        </Reason>
        <Reason color="#8D9091">
          <ReasonGradient color="#8D9091" />
          <ReasonGradient placeOnEnd color="#8D9091" />
          <ReasonContent>
            <ReasonIcon color="#8D9091">
              <MdOutlineRemoveRedEye />
            </ReasonIcon>
            <ReasonLabel color="#8D9091">Increase your visibility</ReasonLabel>
          </ReasonContent>
        </Reason>
        <Reason color="#DC0808">
          <ReasonGradient color="#DC0808" />
          <ReasonGradient color="#DC0808" placeOnEnd />
          <ReasonContent>
            <ReasonIcon color="#DC0808">
              <MdOutlineAccountBalance />
            </ReasonIcon>
            <ReasonLabel color="#DC0808">
              Get access to ideal funding
            </ReasonLabel>
          </ReasonContent>
        </Reason>
      </ReasonsContainer>
      <StartButton />
    </PanelContent>
  </StyledPanel>
)

const FaqContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  margin-top: 64px;
  ${({ theme }) => theme.media.phone} {
    margin-top: 34px;
  }
`
const StyledFaqItem = styled.div`
  display: flex;
  flex-direction: column;
  border-top: solid 1px ${({ theme }) => theme.colors.black};
`
const FaqHeader = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 32px 0;
  user-select: none;
`

const CloseIcon = styled(MdOutlineClose)`
  font-size: 24px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black};
  margin-right: 21px;
`
const OpenIcon = styled(MdAdd)`
  font-size: 24px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black};
  margin-right: 21px;
`

const FaqItem = ({ title, Content }) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <StyledFaqItem>
      <FaqHeader onClick={() => setIsOpen(x => !x)}>
        {isOpen ? <CloseIcon /> : <OpenIcon />}
        {title}
      </FaqHeader>
      <Collapse in={isOpen} unmountOnExit timeout={200}>
        <Content />
      </Collapse>
    </StyledFaqItem>
  )
}

const faqList = [
  { title: 'What type of writing do you publish?', Content: TypeContent },
  { title: 'Is TrialSite a pre-print server?', Content: PrePrintContent },
  {
    title: 'Who reads TrialSite and how much traffic do you get?',
    Content: WhoReadsContent,
  },
  { title: 'How do I build a following?', Content: BuildFollowingContent },
  {
    title: 'How do I get my writing on the home page of TrialSite?',
    Content: HowGetWritingContent,
  },
  {
    title:
      'Why should I write on TrialSite and not somewhere else (like Medium)?',
    Content: WhyShouldContent,
  },
  {
    title: 'I am not good at writing; can you assist me?',
    Content: AssistContent,
  },
]

export const FourthPanel = () => (
  <StyledPanel color="#F1F1F3">
    <PanelContent>
      <PanelTitle style={{ textAlign: 'left' }}>
        Frequently asked
        <br /> questions
      </PanelTitle>
      <FaqContainer>
        {faqList.map((item, index) => (
          <FaqItem key={`faq-${index}`} {...item} />
        ))}
      </FaqContainer>
    </PanelContent>
  </StyledPanel>
)
